import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const PAGE_VIEW_SESSION_KEY = 'pv_session';
let inMemorySessionId: string | null = null;

function createSessionId(): string {
  return crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId(): string {
  if (inMemorySessionId) return inMemorySessionId;

  const fallbackId = createSessionId();

  try {
    const existing = sessionStorage.getItem(PAGE_VIEW_SESSION_KEY);
    if (existing) {
      inMemorySessionId = existing;
      return existing;
    }

    sessionStorage.setItem(PAGE_VIEW_SESSION_KEY, fallbackId);
  } catch {
    // sessionStorage can fail on some Android WebViews during cold start
  }

  inMemorySessionId = fallbackId;
  return fallbackId;
}

function detectDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes('MyCue')) return 'app';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari';
  if (ua.includes('Firefox')) return 'firefox';
  if (ua.includes('Edg')) return 'edge';
  return 'other';
}

interface PendingView {
  id: string;
  session_id: string;
  page_path: string;
  referrer_path: string | null;
  user_id: string;
  device_type: string;
  browser: string;
  screen_width: number;
  duration_seconds: number;
  startTime: number;
}

const pendingQueue: PendingView[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL = 30_000;

function flushQueue() {
  if (pendingQueue.length === 0) return;

  const now = Date.now();
  const rows = pendingQueue.map(({ startTime, ...rest }) => ({
    ...rest,
    duration_seconds: Math.max(1, Math.round((now - startTime) / 1000)),
  }));

  pendingQueue.length = 0;

  supabase
    .from('page_views')
    .insert(rows)
    .then(() => {});
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, FLUSH_INTERVAL);
}

export function usePageViewTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const pagePath = location.pathname;
    const referrerPath = prevPathRef.current;

    const timer = setTimeout(() => {
      const viewId = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      pendingQueue.push({
        id: viewId,
        session_id: getSessionId(),
        page_path: pagePath,
        referrer_path: referrerPath,
        user_id: user.id,
        device_type: detectDeviceType(),
        browser: detectBrowser(),
        screen_width: window.innerWidth,
        duration_seconds: 0,
        startTime: Date.now(),
      });

      scheduleFlush();
    }, 100);

    prevPathRef.current = pagePath;

    return () => clearTimeout(timer);
  }, [location.pathname, user]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flushQueue();
    };
    const handleUnload = () => flushQueue();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);
}
